import React, { useEffect, useState } from 'react';
import { ZoomTable, ZoomBtn, ZoomBadge } from './zoom/ZoomTable';

// Voice Bot wizard — tenant-admin self-service creation of AI voice agents,
// reusing the production voicebot engine (resolves bot by called DID).

type Bot = {
  id: string; bot_name: string; bot_greeting: string; system_prompt: string;
  knowledge_base_id: string | null; kb_name?: string; voice_id: string;
  voice_name?: string; language: string; is_active: boolean; dids: string[];
};
type KB = { id: string; name: string };

const VOICES = [
  { id: 'nova',    label: 'Nova — warm female (default)' },
  { id: 'shimmer', label: 'Shimmer — soft female' },
  { id: 'alloy',   label: 'Alloy — neutral' },
  { id: 'echo',    label: 'Echo — male' },
  { id: 'fable',   label: 'Fable — British male' },
  { id: 'onyx',    label: 'Onyx — deep male' },
];
const LANGS = [
  { id: 'en', label: 'English' }, { id: 'ar', label: 'Arabic' },
  { id: 'hi', label: 'Hindi' },   { id: 'ur', label: 'Urdu' },
  { id: 'fr', label: 'French' },  { id: 'es', label: 'Spanish' },
];

function authHeaders(): Record<string, string> {
  const t = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` };
}

const EMPTY = {
  bot_name: '', bot_greeting: 'Hello, thank you for calling. How may I help you today?',
  system_prompt: 'You are a friendly, professional voice assistant. Keep answers to 1-2 short sentences. Draw on the knowledge base when relevant.',
  language: 'en', kb_mode: 'existing' as 'existing' | 'new' | 'none',
  knowledge_base_id: '', new_kb_name: '', voice_id: '21m00Tcm4TlvDq8ikWAM',
  did_primary: '', did_backup: '',
};

const STEPS = ['Identity', 'Knowledge', 'Voice', 'Numbers', 'Review'];

export default function VoiceBotsPage({ tenants = [], showTenantSelector = false, zoom = false }: any = {}) {
  const [bots, setBots] = useState<Bot[]>([]);
  const [kbs, setKbs] = useState<KB[]>([]);
  const [avail, setAvail] = useState<string[]>([]);
  const [elVoices, setElVoices] = useState<{ voice_id: string; name: string }[]>([]);
  const [wizard, setWizard] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [d, setD] = useState<any>({ ...EMPTY });
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k: string, v: any) => setD((x: any) => ({ ...x, [k]: v }));
  const tenantName = (id: any) => { const t=(tenants||[]).find((x: any)=>x.id===Number(id)); return (t && (t.tenant_name||t.name||t.company_name)) || (id?('Tenant '+id):'\u2014'); };

  const load = async () => {
    try {
      const [b, k, a, v] = await Promise.all([
        fetch('/api/voice-bots', { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/voice-bots/knowledge-bases', { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/voice-bots/available-dids', { headers: authHeaders() }).then(r => r.json()),
        fetch('/api/voice-bots/voices', { headers: authHeaders() }).then(r => r.json()),
      ]);
      setBots(Array.isArray(b) ? b : []);
      setKbs(Array.isArray(k) ? k : []);
      setAvail(Array.isArray(a) ? a : []);
      setElVoices(Array.isArray(v) ? v : []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setD({ ...EMPTY, kb_mode: kbs.length ? 'existing' : 'none' }); setEditId(null); setStep(0); setWizard(true); };
  const openEdit = (b: Bot) => {
    setD({
      bot_name: b.bot_name, bot_greeting: b.bot_greeting, system_prompt: b.system_prompt,
      language: b.language || 'en', kb_mode: b.knowledge_base_id ? 'existing' : 'none',
      knowledge_base_id: b.knowledge_base_id || '', new_kb_name: '',
      voice_id: b.voice_id || 'nova',
      did_primary: b.dids?.[0] || '', did_backup: b.dids?.[1] || '',
    });
    setEditId(b.id); setStep(0); setWizard(true);
  };

  const submit = async () => {
    setBusy(true);
    const dids = [d.did_primary, d.did_backup].filter(Boolean);
    const payload: any = {
      bot_name: d.bot_name.trim(), bot_greeting: d.bot_greeting, system_prompt: d.system_prompt,
      voice_id: d.voice_id, voice_name: (elVoices.find(v => v.voice_id === d.voice_id)?.name) || VOICES.find(v => v.id === d.voice_id)?.label || '',
      language: d.language, dids,
    };
    if (d.kb_mode === 'existing') payload.knowledge_base_id = d.knowledge_base_id || null;
    else if (d.kb_mode === 'new') payload.new_kb_name = d.new_kb_name;
    else payload.knowledge_base_id = null;
    try {
      const url = editId ? `/api/voice-bots/${editId}` : '/api/voice-bots';
      const method = editId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (r.ok) { setStatus(editId ? 'Voice bot updated' : 'Voice bot created'); setWizard(false); await load(); }
      else { const e = await r.json().catch(() => ({})); alert(e.error || 'Save failed'); }
    } catch (e) { alert('Save failed'); }
    setBusy(false);
  };

  const remove = async (b: Bot) => {
    if (!window.confirm(`Delete voice bot "${b.bot_name}"? Its numbers will be released.`)) return;
    await fetch(`/api/voice-bots/${b.id}`, { method: 'DELETE', headers: authHeaders() });
    await load();
  };

  const canNext = () => {
    if (step === 0) return !!d.bot_name.trim();
    if (step === 1 && d.kb_mode === 'new') return !!d.new_kb_name.trim();
    return true;
  };

  // ── DID options: available + (when editing) the bot's already-assigned ones ──
  const didOptions = Array.from(new Set([...avail, d.did_primary, d.did_backup].filter(Boolean)));
  // Real ElevenLabs voices from the account (fallback to the static list).
  const voiceOpts = elVoices.length ? elVoices.map(v => ({ id: v.voice_id, label: v.name })) : VOICES;

  const inp: React.CSSProperties = { width: '100%', padding: '9px 11px', border: '1px solid var(--ce-card-border, #d1d5db)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', color: 'var(--ce-text, #131619)', background: 'var(--ce-input, #fff)' };
  const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ce-text, #374151)', display: 'block', margin: '12px 0 4px' };

  return (
    <>
      {!zoom && <div className="admin-page-title">Voice Bots</div>}
      {!zoom && <div className="admin-page-subtitle">Create AI voice agents (like Krishna) and assign one or two phone numbers for redundancy.</div>}
      {status && <div className="admin-alert">{status}</div>}

      {zoom && (
        <div className="z-zva">
          <div className="z-zva-hero">
            <div className="z-zva-copy">
              <h1 className="z-zva-title">Every call answered. Every customer served. Instantly.</h1>
              <ul className="z-zva-list">
                <li><b>Always on, even after hours</b> — round-the-clock coverage so every caller gets help or a callback.</li>
                <li><b>Smart AI concierge</b> — greets callers, answers their questions, and routes them to the right place.</li>
                <li><b>Knowledge-aware</b> — draws answers from your knowledge base, not just a fixed script.</li>
                <li><b>Natural, on-brand conversations</b> — callers speak normally and get fast, relevant responses.</li>
              </ul>
              <ZoomBtn variant="primary" onClick={openCreate}>+ Create Auto Receptionist</ZoomBtn>
            </div>
            <div className="z-zva-art"><div className="z-zva-badge">AI Virtual Receptionist</div></div>
          </div>

          <ZoomTable
            title="Auto Receptionists"
            columns={[
              { key: 'name', label: 'Name', render: (b: any) => <div className="z-tbl-primary-cell">{b.bot_name}</div> },
              ...(showTenantSelector ? [{ key: 'tenant', label: 'Tenant', render: (b: any) => tenantName(b.tenant_id) }] : []),
              { key: 'greeting', label: 'Greeting', render: (b: any) => <span style={{ color: '#6B7280', display: 'inline-block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.bot_greeting}</span> },
              { key: 'numbers', label: 'Numbers', render: (b: any) => b.dids?.length ? b.dids.map((x: string) => <ZoomBadge key={x} text={x} tone="blue" />) : '—' },
              { key: 'voice', label: 'Voice', render: (b: any) => b.voice_id || 'nova' },
              { key: 'status', label: 'Status', render: (b: any) => <ZoomBadge text={b.is_active ? 'Active' : 'Off'} tone={b.is_active ? 'green' : 'gray'} /> },
            ]}
            rows={bots}
            rowKey={(b: any) => b.id}
            rowActions={(b: any) => (<><ZoomBtn onClick={() => openEdit(b)}>Edit</ZoomBtn><ZoomBtn variant="danger" onClick={() => remove(b)}>Delete</ZoomBtn></>)}
            selectable={false}
            emptyText="No auto receptionists yet — click Create."
          />
        </div>
      )}

      {!zoom && (
      <div className="content-card">
        <div className="admin-card-header">
          <h3>Your Voice Bots <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 400 }}>({bots.length})</span></h3>
          <button className="primary-btn" onClick={openCreate}>+ Create Voice Bot</button>
        </div>
        <table className="admin-table">
          <thead><tr>{showTenantSelector && <th>Tenant</th>}<th>Name</th><th>Greeting</th><th>Knowledge Base</th><th>Voice</th><th>Numbers</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {bots.map(b => (
              <tr key={b.id}>
                {showTenantSelector && <td>{tenantName((b as any).tenant_id)}</td>}
                <td style={{ fontWeight: 600 }}>{b.bot_name}</td>
                <td style={{ maxWidth: 220, color: '#6b7280', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.bot_greeting}</td>
                <td>{b.kb_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                <td>{(b.voice_id || 'nova')}</td>
                <td>{b.dids?.length ? b.dids.map(x => <span key={x} style={{ display: 'inline-block', background: '#eef2ff', color: '#4f46e5', borderRadius: 20, padding: '2px 9px', marginRight: 5, fontSize: 12 }}>{x}</span>) : <span style={{ color: '#9ca3af' }}>none</span>}</td>
                <td>{b.is_active ? <span style={{ color: '#16a34a' }}>● Active</span> : <span style={{ color: '#9ca3af' }}>○ Off</span>}</td>
                <td>
                  <button className="secondary-btn" onClick={() => openEdit(b)}>Edit</button>{' '}
                  <button className="secondary-btn" style={{ color: '#dc2626' }} onClick={() => remove(b)}>Delete</button>
                </td>
              </tr>
            ))}
            {bots.length === 0 && <tr><td colSpan={showTenantSelector ? 8 : 7} style={{ textAlign: 'center', color: '#6b7280', padding: 26 }}>No voice bots yet. Click “Create Voice Bot”.</td></tr>}
          </tbody>
        </table>
      </div>
      )}

      {wizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--ce-card, #fff)', color: 'var(--ce-text, #131619)', borderRadius: 14, width: 640, maxWidth: '94vw', maxHeight: '92vh', overflow: 'auto', padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{editId ? 'Edit Voice Bot' : 'New Voice Bot'}</h3>
              <button className="secondary-btn" onClick={() => setWizard(false)}>✕</button>
            </div>

            {/* stepper */}
            <div style={{ display: 'flex', gap: 6, margin: '14px 0 18px' }}>
              {STEPS.map((s, i) => (
                <div key={s} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 5, borderRadius: 3, background: i <= step ? '#4f46e5' : '#e5e7eb' }} />
                  <div style={{ fontSize: 11, marginTop: 4, color: i === step ? '#4f46e5' : '#9ca3af', fontWeight: i === step ? 700 : 400 }}>{s}</div>
                </div>
              ))}
            </div>

            {step === 0 && (<div>
              <label style={lbl}>Bot name *</label>
              <input style={inp} value={d.bot_name} onChange={e => set('bot_name', e.target.value)} placeholder="e.g. Reception Assistant" />
              <label style={lbl}>Greeting (first thing the caller hears)</label>
              <textarea style={{ ...inp, minHeight: 60 }} value={d.bot_greeting} onChange={e => set('bot_greeting', e.target.value)} />
              <label style={lbl}>Language</label>
              <select style={inp} value={d.language} onChange={e => set('language', e.target.value)}>{LANGS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}</select>
            </div>)}

            {step === 1 && (<div>
              <label style={lbl}>Personality / instructions (system prompt)</label>
              <textarea style={{ ...inp, minHeight: 90 }} value={d.system_prompt} onChange={e => set('system_prompt', e.target.value)} />
              <label style={lbl}>Knowledge base</label>
              <div style={{ display: 'flex', gap: 14, margin: '4px 0 8px', fontSize: 13 }}>
                <label><input type="radio" checked={d.kb_mode === 'existing'} onChange={() => set('kb_mode', 'existing')} /> Use existing</label>
                <label><input type="radio" checked={d.kb_mode === 'new'} onChange={() => set('kb_mode', 'new')} /> Create new</label>
                <label><input type="radio" checked={d.kb_mode === 'none'} onChange={() => set('kb_mode', 'none')} /> None</label>
              </div>
              {d.kb_mode === 'existing' && (
                <select style={inp} value={d.knowledge_base_id} onChange={e => set('knowledge_base_id', e.target.value)}>
                  <option value="">— select a knowledge base —</option>
                  {kbs.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
                </select>
              )}
              {d.kb_mode === 'new' && (
                <input style={inp} value={d.new_kb_name} onChange={e => set('new_kb_name', e.target.value)} placeholder="New knowledge base name (add documents later)" />
              )}
              {d.kb_mode === 'none' && <div style={{ fontSize: 12, color: '#9ca3af' }}>Bot will answer from its instructions only (no document knowledge).</div>}
            </div>)}

            {step === 2 && (<div>
              <label style={lbl}>Voice</label>
              <select style={inp} value={d.voice_id} onChange={e => set('voice_id', e.target.value)}>{voiceOpts.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}</select>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>The voice used to speak to callers.</div>
            </div>)}

            {step === 3 && (<div>
              <label style={lbl}>Primary number</label>
              {didOptions.length ? (
                <select style={inp} value={d.did_primary} onChange={e => set('did_primary', e.target.value)}>
                  <option value="">— none —</option>
                  {didOptions.map(x => <option key={x} value={x} disabled={x === d.did_backup}>{x}</option>)}
                </select>
              ) : <div style={{ fontSize: 13, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 10 }}>No unassigned numbers available. Add DIDs on the <b>DIDs</b> page first, then assign them here.</div>}
              <label style={lbl}>Backup number (optional — for redundancy)</label>
              <select style={inp} value={d.did_backup} onChange={e => set('did_backup', e.target.value)} disabled={!didOptions.length}>
                <option value="">— none —</option>
                {didOptions.map(x => <option key={x} value={x} disabled={x === d.did_primary}>{x}</option>)}
              </select>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>Both numbers route callers to this bot. Assign two for redundancy across trunks.</div>
            </div>)}

            {step === 4 && (<div style={{ fontSize: 14 }}>
              <Row k="Name" v={d.bot_name} />
              <Row k="Greeting" v={d.bot_greeting} />
              <Row k="Language" v={LANGS.find(l => l.id === d.language)?.label} />
              <Row k="Knowledge" v={d.kb_mode === 'existing' ? (kbs.find(k => k.id === d.knowledge_base_id)?.name || '—') : d.kb_mode === 'new' ? `New: ${d.new_kb_name}` : 'None'} />
              <Row k="Voice" v={voiceOpts.find(v => v.id === d.voice_id)?.label || d.voice_id} />
              <Row k="Numbers" v={[d.did_primary, d.did_backup].filter(Boolean).join(', ') || 'none'} />
            </div>)}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button className="secondary-btn" onClick={() => step === 0 ? setWizard(false) : setStep(step - 1)}>{step === 0 ? 'Cancel' : 'Back'}</button>
              {step < STEPS.length - 1
                ? <button className="primary-btn" disabled={!canNext()} onClick={() => setStep(step + 1)}>Next</button>
                : <button className="primary-btn" disabled={busy} onClick={submit}>{busy ? 'Saving…' : (editId ? 'Save Changes' : 'Create Voice Bot')}</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ k, v }: { k: string; v: any }) {
  return (
    <div style={{ display: 'flex', padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
      <div style={{ width: 130, color: '#6b7280', fontWeight: 600 }}>{k}</div>
      <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{v || '—'}</div>
    </div>
  );
}
